/**
 * Compte actif PAR ONGLET (multi-login).
 *
 * Le slot actif vit dans `sessionStorage` (pas `localStorage`) → chaque onglet a
 * son propre compte actif. Il est désigné au backend par l'en-tête `X-Active-Slot`,
 * avec repli `?slot=N` pour les requêtes browser-natives (téléchargement — qui ne
 * peuvent pas poser d'en-tête).
 *
 * Cache mémoire + store observable, tolérant à un storage indisponible (jamais
 * d'exception : mode privé, quotas, environnement de test sans sessionStorage).
 */

/** En-tête HTTP portant le slot actif. */
export const SLOT_HEADER = 'X-Active-Slot';
/** Paramètre de repli pour les requêtes browser-natives. */
export const SLOT_QUERY = 'slot';

const STORAGE_KEY = 'xfile.activeSlot';

function readStorage(): number | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isInteger(n) && n >= 0 ? n : null;
  } catch {
    return null;
  }
}

let current: number | null = readStorage();
const subscribers = new Set<() => void>();

/** Slot actif courant (ou `null` si aucun compte actif dans cet onglet). */
export function getActiveSlot(): number | null {
  return current;
}

/** Change le slot actif : persiste, met à jour le cache, notifie les abonnés. No-op si inchangé. */
export function setActiveSlot(slot: number | null): void {
  if (slot === current) return;
  current = slot;
  try {
    if (slot === null) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, String(slot));
  } catch {
    // Storage indisponible : le cache mémoire suffit pour la session courante.
  }
  for (const cb of subscribers) cb();
}

/** Abonnement aux changements de slot actif (retourne la fonction de désabonnement). */
export function subscribeActiveSlot(cb: () => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

/** Ajoute `?slot=N` à une URL browser-native (téléchargement). */
export function withSlotParam(url: string): string {
  const slot = getActiveSlot();
  if (slot === null) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${SLOT_QUERY}=${slot}`;
}
